/* Diamond sold-out availability checker — shared by the ring builder, cart page and cart drawer.
   Diamonds come from an external feed (py.preciouscarbon.com) and can sell out after a shopper
   selects one. This module re-checks a diamond against the feed and exposes helpers to remove it
   from the cart / builder selection and redirect the shopper to the matching diamond search page. */
(function () {
  if (window.ROADiamond) return; // idempotent — loaded once from theme.liquid

  var API_URL = "https://py.preciouscarbon.com/roa/graphql";
  var TIMEOUT_MS = 6000;
  var SOLD_OUT_RE = /sold|out of stock|unavailable/i;

  /* API diamondType -> diamond search page.

     Under /collections/, which is where these listings actually live: the bare
     /lab-diamonds is a 301 to /collections/lab-diamonds, and more importantly the Vue
     router's base is derived from the path (getBaseUrl in assets/ring-builder.js
     returns "/collections/" for a collections URL). The ":diamondType/:shape" route
     therefore only resolves under that prefix — /collections/lab-diamonds/radiant is a
     real page, while the bare /lab-diamonds/radiant is a 404. */
  var TYPE_TO_SEARCH_URL = {
    white: "/collections/natural-diamonds",
    white_color: "/collections/natural-fancy-coloured-diamonds",
    lab: "/collections/lab-diamonds",
    lab_color: "/collections/coloured-lab-grown-diamonds",
  };

  // Shopify product_type -> API diamondType (mirrors the stone_type map in diamond-detail.js).
  var PRODUCT_TYPE_TO_API_TYPE = {
    "Lab Diamonds": "lab",
    "Lab Colour Diamonds": "lab_color",
    "Natural Diamonds": "white",
    "Natural Colour Diamonds": "white_color",
  };

  /* POST a diamondById query to the feed, asking for `fields`, with the shared timeout.
     Resolves with the parsed JSON body; rejects on network error / timeout / non-2xx. */
  function queryDiamondById(ref, fields) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () {
      ctrl.abort();
    }, TIMEOUT_MS);

    var query =
      '{ diamondById(diamondId:"' +
      ref.diamondId +
      '",diamondType:"' +
      ref.diamondType +
      '") { diamond { ' +
      fields +
      " } } }";

    return fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: query }),
      signal: ctrl.signal,
    })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(
        function (json) {
          clearTimeout(timer);
          return json;
        },
        function (e) {
          clearTimeout(timer);
          throw e;
        }
      );
  }

  /* Re-check a single diamond against the feed.
     Returns Promise<'available' | 'soldout' | 'unknown'> and never rejects. */
  function checkAvailability(ref) {
    if (!ref || !ref.diamondId || !ref.diamondType) return Promise.resolve("unknown");
    if (window.ROADiamond._forceSoldOut) return Promise.resolve("soldout"); // test hook

    return queryDiamondById(ref, "diamondId availability")
      .then(function (json) {
        if (!json || !json.data || json.errors) return "unknown"; // malformed / GraphQL error -> fail-open
        var node = json.data.diamondById;
        if (!node) return "soldout"; // id not reachable under its recorded type -> gone
        var arr = node.diamond;
        if (!Array.isArray(arr) || !arr.length) return "soldout"; // dropped from the feed
        // Defensive: the feed must echo back the exact diamond we asked for. If it returns
        // a record under a different id (a stale/reassigned sku, a feed glitch), treat it
        // the same as sold out rather than trusting a mismatched record as "available".
        if (String(arr[0].diamondId) !== String(ref.diamondId)) return "soldout";
        if (SOLD_OUT_RE.test(String(arr[0].availability || ""))) return "soldout"; // defensive
        return "available";
      })
      .catch(function (e) {
        console.warn("[ROADiamond] availability check failed (network/CORS/timeout), failing open:", e);
        return "available"; // network error / timeout / CORS -> fail-open
      });
  }

  /* Fetch a diamond's live record from the diamond detail API — the same diamondById
     query the ring builder uses. finalPriceAud here is the diamond's real current price:
     it's what the builder creates the Shopify product at, so it's what the cart line
     should be charging.
     Returns Promise<{ status: 'available', diamond } | { status: 'soldout' } | { status: 'unknown' }>
     and never rejects. */
  function fetchDiamondDetails(ref) {
    if (!ref || !ref.diamondId || !ref.diamondType) return Promise.resolve({ status: "unknown" });

    return queryDiamondById(ref, "diamondId finalPriceAud availability shape caratWeight diamondImage")
      .then(function (json) {
        if (!json || !json.data || json.errors) return { status: "unknown" };
        var node = json.data.diamondById;
        var arr = node && node.diamond;
        if (!Array.isArray(arr) || !arr.length) return { status: "soldout" };
        var diamond = arr[0];
        if (String(diamond.diamondId) !== String(ref.diamondId)) return { status: "soldout" };
        if (SOLD_OUT_RE.test(String(diamond.availability || ""))) return { status: "soldout" };
        return { status: "available", diamond: diamond };
      })
      .catch(function (e) {
        console.warn("[ROADiamond] diamond detail fetch failed (network/CORS/timeout):", e);
        return { status: "unknown" };
      });
  }

  /* Shapes the diamond listing is routable by. The builder routes it as
     <listing>/<shape> (the ":diamondType" / ":shape" pair in assets/ring-builder.js)
     and applies that segment as the shape filter on arrival (updateDiamondShape in
     ring-builder-choose-diamonds.js, which lower-cases it, capitalises the first letter
     and ignores anything outside this list). Mirrors getDiamondShapes there.

     All four listings serve these — verified against the live site 2026-09-23:
         /collections/lab-diamonds/radiant                  200
         /collections/natural-diamonds/radiant              200
         /collections/coloured-lab-grown-diamonds/radiant   200
         /collections/natural-fancy-coloured-diamonds/radiant 200
     (the same pattern templates/index.json already links its homepage shape tiles to).
     Note it is the /collections/ prefix that makes them resolve — see the comment on
     TYPE_TO_SEARCH_URL above. */
  var LISTING_SHAPES = [
    "round",
    "oval",
    "cushion",
    "emerald",
    "princess",
    "radiant",
    "pear",
    "marquise",
    "asscher",
    "heart",
  ];

  /* The diamond search page for a type, pre-filtered to `shape` when one is given and
     routable — so a shopper sent here because their stone sold lands on the shape they
     were actually shopping for instead of the unfiltered list. An unknown or missing
     shape just yields the plain listing. */
  function typeToSearchUrl(t, shape) {
    var known = TYPE_TO_SEARCH_URL[t];
    var base = known || TYPE_TO_SEARCH_URL.white;
    // An unknown type means `base` is only a fallback guess — don't compound it with a
    // shape segment that may not resolve on whatever listing that turns out to be.
    if (!known) return base;
    var s = String(shape == null ? "" : shape).trim().toLowerCase();
    return LISTING_SHAPES.indexOf(s) === -1 ? base : base + "/" + s;
  }

  /* Shape off a cart line (properties.stone_shape) or a feed object (shape). */
  function diamondShapeOf(d) {
    if (!d) return "";
    var p = d.properties || d;
    return p.stone_shape || p.shape || "";
  }

  function productTypeToApiType(pt) {
    return PRODUCT_TYPE_TO_API_TYPE[pt] || null;
  }

  function isDiamondLineItem(item) {
    return !!productTypeToApiType(item && item.product_type);
  }

  // Cents-precision epsilon — avoid flagging on float rounding noise from the cart.
  var PRICE_EPSILON_CENTS = 1;

  // finalPriceAud is always in AUD, so it can only be compared with a cart priced in AUD.
  var API_PRICE_CURRENCY = "AUD";

  /* Prefer the real Shopify variant sku — it's set directly from the diamond's id at
     product-creation time and is what Shopify itself tracks. The custom line-item
     property of the same name can drift/go stale (confirmed via a live cart:
     properties.sku pointed at a different diamond than the actual variant sku /
     displayed certificate), so it's only a fallback here. */
  function diamondIdFromLineItem(item) {
    return item.sku || (item.properties && item.properties.sku);
  }

  /* The line's own unit price before discounts, in cents. That's the variant price the
     product was created at — the lab-diamond 40% off is a discount on top of it, so it
     has to stay out of the comparison with finalPriceAud. */
  function linePriceCents(item) {
    return typeof item.original_price === "number" ? item.original_price : item.price;
  }

  /* checkCartLinePrice used to take just cart.currency. It now takes the whole /cart.js
     payload (the line discount below needs cart.original_total_price), so accept both. */
  function cartCurrencyOf(cart) {
    if (typeof cart === "string") return cart;
    return cart && cart.currency;
  }

  /* The discount rate the CART LINE ITSELF renders, so the popup can quote the same
     numbers the shopper is looking at on the page.
     Mirrors sections/cart-template.liquid (~line 1122): a lab diamond line prints its
     original price struck out and 40% off it in green, once the cart's original total
     reaches $500. Comparing against finalPriceAud still uses the undiscounted price —
     this only affects what is DISPLAYED. */
  function lineDiscountRate(item, cart) {
    var isLab = item.product_type === "Lab Diamonds" || item.product_type === "Lab Colour Diamonds";
    if (!isLab) return 1;
    var totalCents = cart && typeof cart.original_total_price === "number" ? cart.original_total_price : 0;
    return totalCents / 100 >= 500 ? 0.6 : 1;
  }

  /* Check one diamond cart line against that diamond's current price in the diamond
     detail API (finalPriceAud). A Shopify cart line always charges its variant's price,
     and the builder creates that variant once, at the API price of the moment — so when
     the API price moves afterwards, the cart keeps charging the old one.
     `cart` is the whole /cart.js payload (a bare currency string is still accepted).
     Returns Promise<{ status: 'ok' | 'changed' | 'soldout' | 'unknown', changed }> and
     never rejects. `changed` (only on 'changed') is the entry the price-change popups
     show and repriceCartLine() takes. */
  function checkCartLinePrice(item, cart) {
    var cartCurrency = cartCurrencyOf(cart);
    // A cart in another market's currency shows converted prices, which never match.
    if (cartCurrency && cartCurrency !== API_PRICE_CURRENCY) return Promise.resolve({ status: "unknown" });

    var ref = { diamondId: diamondIdFromLineItem(item), diamondType: productTypeToApiType(item.product_type) };
    return fetchDiamondDetails(ref).then(function (result) {
      // Sold-out lines are verifyCartDiamonds()'s job — no price to sync for those.
      if (result.status !== "available") return { status: result.status };
      var apiCents = Math.round(parseFloat(result.diamond.finalPriceAud) * 100);
      if (!isFinite(apiCents) || apiCents <= 0) return { status: "unknown" };

      var lineCents = linePriceCents(item);
      // Test hook: pretend the line is $8 under the API price so QA can run the whole
      // popup + reprice flow on demand. The reprice itself still uses the real API price.
      if (window.ROADiamond._forcePriceChange) lineCents -= 800;
      if (Math.abs(apiCents - lineCents) <= PRICE_EPSILON_CENTS) return { status: "ok" };

      /* What the popup DISPLAYS has to be the figure the shopper can see on the cart
         line, not the raw unit price we compare against the API:
           • × quantity   — the cart prints line totals, not unit prices
           • × discount   — a lab diamond line prints its 40%-off price in green, so
                            quoting the undiscounted one looked like a wrong number
         Both sides get the same treatment, so the before/after delta stays honest. */
      var qty = item.quantity || 1;
      var rate = lineDiscountRate(item, cart);

      return {
        status: "changed",
        changed: {
          label: describeDiamond(item),
          diamondType: ref.diamondType,
          lineKey: item.key,
          variantId: item.variant_id,
          diamondId: ref.diamondId,
          previousPrice: lineCents, // cents — what the cart line is charging right now
          updatedPrice: apiCents, // cents — the diamond's current price in the API
          previousPriceDisplay: Math.round(lineCents * qty * rate), // cents — as the cart shows it
          updatedPriceDisplay: Math.round(apiCents * qty * rate), // cents — as the cart will show it
          // Line totals BEFORE the discount — the struck-out figure on the cart line.
          // Equal to the *Display pair above when no discount applies.
          previousPriceFull: Math.round(lineCents * qty),
          updatedPriceFull: Math.round(apiCents * qty),
          discountApplied: rate < 1,
          diamond: result.diamond, // the API record the new product is created from
          productTitle: item.product_title,
          productType: item.product_type,
          image: item.image,
          properties: item.properties,
          quantity: qty,
        },
      };
    });
  }

  /* Cart-level orchestrator for price drift, mirroring verifyCartDiamonds() below.
     Returns Promise<'ok' | 'changed' | 'unverified'> and never rejects.
       'ok'        — no diamond lines, or all prices match the API
       'changed'   — one or more lines' price differs from the API price; the price-change
                     modal is shown (see showPriceChangeModal in cart-template.liquid) and
                     the line is repriced (repriceCartLine below) when the shopper closes
                     it — nothing is changed here, this only detects + surfaces it
       'unverified'— at least one line could not be checked; caller decides

     opts.force skips the one-shot 'priceSkipNext' guard below. The CHECKOUT GATE must
     always pass it: the guard exists to stop the page-load check re-opening the modal
     right after a reprice, and a checkout attempt that landed in the window between
     closeAndSync() setting the flag and its reload firing used to eat the flag and be
     waved through against the stale price. */
  function verifyCartDiamondPrices(opts) {
    /* One-shot skip guard — set by the price-change modal's closeAndSync before it
       calls window.location.reload() so the very next load (which follows a successful
       reprice) does not re-open the modal against the now-correct price.
       Works exactly like the gift system's giftSkipNext flag. */
    if (!(opts && opts.force) && sessionStorage.getItem("priceSkipNext")) {
      sessionStorage.removeItem("priceSkipNext");
      return Promise.resolve("ok");
    }

    // A cart that cannot be read is 'unverified' (the catch below), never 'ok'.
    return readCart({ retries: 2 })
      .then(function (cart) {
        var lines = (cart.items || []).filter(isDiamondLineItem);
        if (!lines.length) return "ok";

        return Promise.all(
          lines.map(function (item) {
            return checkCartLinePrice(item, cart);
          })
        ).then(function (results) {
          var anyUnknown = results.some(function (r) {
            return r.status === "unknown";
          });
          var changedList = results
            .filter(function (r) {
              return r.status === "changed";
            })
            .map(function (r) {
              return r.changed;
            });

          if (!changedList.length) return anyUnknown ? "unverified" : "ok";

          /* A price change is a FACT once we get here, so the return value has to be
             'changed' whatever the popup does. Letting a render error escape made the
             whole promise reject, the caller read 'unverified', and — because the
             checkout gate used to ignore an unverified PRICE — the shopper went
             through to checkout at the stale price with no popup ever shown. */
          try {
            if (window.cartUI && window.cartUI.showPriceChangeModal) {
              window.cartUI.showPriceChangeModal(changedList);
            } else {
              console.error("[ROADiamond] price change detected but cartUI.showPriceChangeModal is unavailable");
            }
          } catch (e) {
            console.error("[ROADiamond] price-change modal failed to render:", e);
          }
          /* Belt and braces: whatever happened to the popup, this page must not be able
             to reach checkout on the stale price. */
          try {
            if (window.cartUI && window.cartUI.blockCheckout) window.cartUI.blockCheckout("price");
          } catch (e) {
            /* ignore — the gate below still blocks on the 'changed' status */
          }
          return "changed";
        });
      })
      .catch(function (e) {
        console.error("[ROADiamond] cart price check failed:", e);
        return "unverified";
      });
  }

  function routeUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return root + path;
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* Create a fresh hidden Shopify product for the diamond at its current API price,
     through the same backend endpoint and payload the ring builder uses
     (createCustomProductWithMetafields in ring-builder-diamond-detail.js). The title and
     product type are carried over from the existing cart line so the new line reads
     exactly like the old one.
     Resolves with the created product ({ handle, variant: { id } }); rejects on failure. */
  function createDiamondProduct(changed) {
    var endpoint = window.ShopifyConfig && window.ShopifyConfig.productCreateApiEndpoint;
    if (!endpoint) return Promise.reject(new Error("No productCreateApiEndpoint configured"));

    var diamond = changed.diamond || {};
    var props = changed.properties || {};
    var shape = diamond.shape || props.stone_shape;
    var carat = diamond.caratWeight || props.carat_weight;
    var image = diamond.diamondImage || changed.image;

    var product = {
      title: changed.productTitle,
      vendor: "Rings Of Australia",
      product_type: changed.productType,
      status: "active",
      tags: "Diamonds,hidden," + (shape || "Shape") + "," + carat + "-carat",
      variants: [
        {
          option1: "Diamond Certificate: " + changed.diamondId,
          price: diamond.finalPriceAud,
          sku: changed.diamondId,
        },
      ],
    };
    if (image) product.images = [{ src: image }];

    return fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ product: product }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.product || !data.product.variant || !data.product.variant.id) {
          throw new Error("Failed to create repriced diamond product: " + JSON.stringify((data && data.errors) || data));
        }
        return data.product;
      });
  }

  /* A newly created product takes a moment to reach the storefront. Poll it the way the
     builder does (checkStorefrontWithRetry) before adding it; never rejects — resolves
     true once /products/<handle>.js reports it available, false when it never does
     within the backoff schedule (0.8s, 1.5s, 3s, 5s). Same URL every time: the old
     ?cacheBust= query string forced Shopify's CDN to miss on every poll, which is
     origin load the store cannot spare; cache: 'no-store' keeps the browser from
     replaying a 404 it saw a moment earlier without touching the CDN. */
  var STOREFRONT_WAITS_MS = [800, 1500, 3000, 5000];
  function waitForStorefrontProduct(handle) {
    function attempt(n) {
      return fetch(routeUrl("products/" + handle + ".js"), { cache: "no-store" })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data || !data.available) throw new Error("Product not ready yet");
          return true;
        })
        .catch(function () {
          if (n >= STOREFRONT_WAITS_MS.length) return false;
          return delay(STOREFRONT_WAITS_MS[n]).then(function () {
            return attempt(n + 1);
          });
        });
    }
    return attempt(0);
  }

  function httpError(status) {
    var err = new Error("HTTP " + status);
    err.status = status;
    return err;
  }

  /* The cart, or a rejection. Through window.ROACart.get (snippets/cart-fetch-dedupe.liquid)
     when it is on the page: one shared request, a 429 waited out opts.retries times
     (opts.settled: after the cart writes already in the air have finished), and a non-2xx
     answer rejected instead of handed over as the cart. Taken for a cart, a 429 body has
     no items — the checks below then reported 'ok' for a cart they never saw, and the
     reprice / sold-out removals decided the line was already gone. */
  function readCart(opts) {
    if (window.ROACart && typeof window.ROACart.get === "function") {
      return window.ROACart.get(opts);
    }
    return fetch(routeUrl("cart.js"), { headers: { Accept: "application/json" }, cache: "no-store" })
      .then(function (r) {
        if (!r.ok) throw httpError(r.status);
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error("Unexpected /cart.js payload");
        return cart;
      });
  }

  /* /cart/change.js through the same policy (window.ROACart.change): a 429 is backed off
     per Retry-After instead of failing the removal outright. One plain attempt when the
     wrapper is missing. Rejects unless Shopify accepted the change. */
  function changeCartLine(payload) {
    if (window.ROACart && typeof window.ROACart.change === "function") {
      return window.ROACart.change(payload, { url: routeUrl("cart/change.js") });
    }
    return fetch(routeUrl("cart/change.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw httpError(res.status);
      return res.json();
    });
  }

  /* Remove every line `pick(cart)` returns, one at a time (the cart API dislikes parallel
     changes), keyed off the cart as it is NOW. The read waits for cart writes already in
     the air: one that touches a line's properties (the delivery-date stamp) makes Shopify
     rebuild that line's key, and a remove sent with the old key is refused (400 "no valid
     id or line parameter") — a 429 backoff, which re-sends the same key seconds later,
     made that likely. If a key dies anyway, the cart is read again and whatever is still
     there is picked once more. Resolves with the number of lines removed; rejects on any
     other failure, with .removed set. */
  function removeLines(pick) {
    var removed = 0;
    function pass(mayRetry) {
      return readCart({ retries: 2, settled: true })
        .then(function (cart) {
          return pick(cart).reduce(function (chain, it) {
            return chain.then(function () {
              return changeCartLine({ id: it.key, quantity: 0 }).then(function () {
                removed++;
              });
            });
          }, Promise.resolve());
        })
        .catch(function (e) {
          if (mayRetry && e && e.status === 400) return pass(false);
          if (e && typeof e === "object") e.removed = removed;
          throw e;
        });
    }
    return pass(true).then(function () {
      return removed;
    });
  }

  /* /cart/add.js through the shared retry policy (window.ROACart.add,
     snippets/cart-fetch-dedupe.liquid): a 429 is backed off per Retry-After with
     jitter (max 4 attempts, ~15s), a 422 "sold out / unavailable" (the new product
     still propagating) is retried a few times, anything else fails at once.
     Resolves with the add response. */
  function addCartLineWithRetry(item) {
    var payload = { items: [item] };
    if (window.ROACart && typeof window.ROACart.add === "function") {
      return window.ROACart.add(payload, { url: routeUrl("cart/add.js") });
    }
    // No wrapper on the page: one plain attempt, no retry.
    return fetch(routeUrl("cart/add.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (res.ok) return data;
        throw new Error(
          (data && (data.description || data.message)) || "Failed to add repriced diamond line"
        );
      });
    });
  }

  /* Move one price-changed cart line onto the diamond's current API price:
       1. create a new Shopify product at finalPriceAud (backend endpoint),
       2. add its variant to the cart with the same line properties,
       3. only once that add is confirmed, remove the old stale-priced line.
     SAFETY: the diamond is in the cart under one line or the other at every point — a
     failed/slow create or add leaves the original line untouched.

     WHY we identify the new line by variant_id and NOT by the _price_synced property:
       On the FIRST reprice the new line gets _price_synced:"true" and the old line is
       removed.  On the SECOND reprice the cart line that is now "old" is the one from
       the first reprice — it ALREADY carries _price_synced:"true".  Skipping by that
       property would therefore skip the old line too, leaving both in the cart and
       doubling the price.  The new variant_id is brand-new every time (a fresh Shopify
       product is created at the updated price), so it is the only safe discriminator. */
  function repriceCartLine(changed) {
    /* Capture the new variant ID as soon as the product is created so the remove
       step can exclude that specific line rather than any _price_synced line. */
    var newVariantId = null;

    return createDiamondProduct(changed)
      .then(function (product) {
        newVariantId = product.variant.id;
        return waitForStorefrontProduct(product.handle).then(function (ready) {
          /* Adding a product the storefront cannot see yet only bounces (422). Stop
             here — the original line is still in the cart, untouched. */
          if (!ready) {
            throw new Error(
              "Repriced diamond product did not become available in time; original line left in place"
            );
          }
          /* Copy the original properties, stripping any stale _price_synced value so it
             does not accumulate across multiple reprices, then stamp a fresh one. */
          var freshProps = {};
          var src = changed.properties || {};
          for (var k in src) {
            if (Object.prototype.hasOwnProperty.call(src, k) && k !== "_price_synced") {
              freshProps[k] = src[k];
            }
          }
          freshProps._price_synced = "true";

          return addCartLineWithRetry({
            id: newVariantId,
            quantity: changed.quantity || 1,  // preserve original qty — never increase it
            properties: freshProps,
          });
        });
      })
      .then(function () {
        /* Re-fetch the live cart so we remove the old line by its CURRENT key.
           syncDeliveryDateLineProperties() writes properties in the background; Shopify
           rebuilds the key on every property write (variant_id:hash(props)), so the key
           stored at price-check time can already be stale.

           Through removeLines, not a bare fetch: that took a 429 body for the cart,
           found no old line in it and reported the reprice a success — and the reload
           that follows skips the price check, with BOTH lines still in the cart.

           Identification logic:
             • Skip the line whose variant_id matches the one we just added — that is
               the new correctly-priced line we must keep.
             • Every other diamond line whose SKU matches the diamond's id is stale and
               goes. Normally exactly one; a retry after a reprice that was cut off
               between its add and its remove (a 429 that never cleared) leaves two.

           This works correctly on every reprice (1st, 2nd, Nth) because we use the
           variant_id of THIS reprice's product, not a property that persists across them.
           Removal is by current key, quantity always 0 — never increase qty. */
        return removeLines(function (cart) {
          return (cart.items || []).filter(function (it) {
            /* Skip the line we just added (identified by its brand-new variant_id). */
            if (it.variant_id === newVariantId) return false;
            if (!isDiamondLineItem(it)) return false;
            /* Match the old line by diamond ID / SKU. */
            var props = it.properties || {};
            var sku = it.sku || props.sku;
            return String(sku) === String(changed.diamondId);
          });
        }).then(
          function (removed) {
            if (removed) return;
            /* Already gone — nothing to remove, reprice succeeded. */
            console.warn(
              "[ROADiamond] repriceCartLine: old line not found after re-fetch " +
              "(diamondId=" + changed.diamondId + "). It may have already been removed."
            );
          },
          function (e) {
            throw new Error(
              "Failed to remove the old diamond line (" + (e && e.status ? "HTTP " + e.status : e && e.message) + ")"
            );
          }
        );
      });
  }

  /* Drop the sold-out diamond lines from the cart.

     Resolves each line against the cart AS IT IS NOW instead of trusting the `key`
     recorded when the check ran. Shopify rebuilds a line's key (variant_id:hash(props))
     on every property write, and syncDeliveryDateLineProperties() stamps _delivery_date
     onto every line in the background of the very same page load — so the recorded key
     is routinely dead by the time the shopper clicks through the popup, and
     /cart/change.js against a dead key removes NOTHING while still answering 200. That
     is why the sold-out stone stayed in the cart. repriceCartLine() already re-resolves
     for exactly this reason; this is the same treatment.

     Matched on the diamond id (the variant sku, which is set from the diamond id at
     product-creation time), which survives property writes. Falls back to the recorded
     keys if nothing matches, so it can never do less than the old behaviour. Read and
     removals go through removeLines: a 429 is waited out rather than taken for an empty
     cart, and the delivery-date stamp landing mid-removal no longer leaves the stone in.
     Resolves with the number of lines actually removed; never rejects. */
  function removeSoldOutLines(soldList) {
    var wanted = {};
    (soldList || []).forEach(function (s) {
      if (s && s.diamondId) wanted[String(s.diamondId)] = true;
    });
    var firstLook = true;

    return removeLines(function (cart) {
      var targets = (cart.items || []).filter(function (it) {
        var id = diamondIdFromLineItem(it);
        return id && wanted[String(id)];
      });

      // First look only: on a second pass, nothing matching means nothing is left.
      if (!targets.length && firstLook) {
        console.warn("[ROADiamond] no sold-out line matched by diamond id; falling back to recorded keys");
        targets = (soldList || [])
          .filter(function (s) {
            return s && s.lineKey;
          })
          .map(function (s) {
            return { key: s.lineKey };
          });
      }
      firstLook = false;
      return targets;
    }).catch(function (e) {
      console.error("[ROADiamond] sold-out removal failed:", e);
      return (e && e.removed) || 0;
    });
  }

  /* Remove the sold-out stone from the builder's sessionStorage selection — only if it matches. */
  function clearBuilderSelection(diamondId) {
    ["selectedDiamond", "selectedSecondDiamond"].forEach(function (key) {
      try {
        var d = JSON.parse(sessionStorage.getItem(key) || "null");
        if (d && String(d.diamondId) === String(diamondId)) {
          sessionStorage.removeItem(key);
          if (key === "selectedSecondDiamond") sessionStorage.removeItem("searchingSecondDiamond");
        }
      } catch (e) {
        /* ignore parse errors */
      }
    });
    // Surgically blank out the DIAMOND entry (and the Ring/thirdStep entry, which can only
    // exist once both a setting AND a diamond are chosen — it's just as invalid as the
    // diamond once that diamond is gone) in the cached header step-bar. The Setting entry
    // stays intact — a sold-out diamond doesn't invalidate an already-chosen, still-valid
    // setting. This has to happen here, at detection time, before the sold-out redirect:
    // the listing page's header is built by an `immediate: true` route watcher that fires
    // before the page's own mounted() hook, so clearing on arrival is always too late.
    try {
      var stepData = JSON.parse(sessionStorage.getItem("stepData") || "null");
      if (stepData) {
        Object.keys(stepData).forEach(function (k) {
          if (stepData[k] && (stepData[k].headerTitle === "DIAMOND" || stepData[k].headerTitle === "Ring")) {
            stepData[k] = { headerTitle: stepData[k].headerTitle };
          }
        });
        sessionStorage.setItem("stepData", JSON.stringify(stepData));
      }
    } catch (e) {
      /* ignore parse errors */
    }
    try {
      var sticky = JSON.parse(sessionStorage.getItem("stickyBarItems") || "null");
      if (Array.isArray(sticky)) {
        sticky = sticky.filter(function (item) {
          return item && item.page !== "diamond";
        });
        sessionStorage.setItem("stickyBarItems", JSON.stringify(sticky));
      }
    } catch (e) {
      /* ignore parse errors */
    }
  }

  /* Human-readable label for popups, from a feed diamond object OR a cart line item. */
  function describeDiamond(d) {
    if (!d) return "The selected diamond";
    var p = d.properties || d; // cart line vs feed object
    var carat = p.carat_weight || p.caratWeight;
    var shape = p.stone_shape || p.shape;
    var type =
      p.stone_type ||
      {
        lab: "Lab Grown Diamond",
        lab_color: "Coloured Lab Grown Diamond",
        white: "Natural Diamond",
        white_color: "Coloured Natural Diamond",
      }[p.diamondType] ||
      "Diamond";
    var cert = p.certificate_number || p.certificateNumber;
    var parts = [];
    if (carat) parts.push(parseFloat(carat) + " ct");
    if (shape) parts.push(shape);
    parts.push(type);
    var label = parts.join(" ");
    return cert ? label + " (Cert. " + cert + ")" : label;
  }

  /* Cart-level orchestrator used by the cart-page load check and the checkout gates.
     Fetches /cart.js and checks every diamond line. Returns Promise<'ok' | 'soldout' | 'unverified'>:
       'ok'         — no diamond lines, or all confirmed available
       'soldout'    — one or more sold out: NOT yet removed — the sold-out popup is shown
                      (or a one-shot notice is stored + navigation to /cart initiated) and
                      the actual removal + gift re-check happens when the shopper clicks
                      through the popup (see showSoldOutModal in cart-template.liquid)
       'unverified' — at least one line could not be verified (feed down); caller decides (checkout blocks) */
  function verifyCartDiamonds() {
    // A cart that cannot be read is 'unverified' (the catch below), never 'ok'.
    return readCart({ retries: 2 })
      .then(function (cart) {
        var lines = (cart.items || []).filter(isDiamondLineItem);
        if (!lines.length) return "ok";

        return Promise.all(
          lines.map(function (item) {
            var diamondId = diamondIdFromLineItem(item);
            var diamondType = productTypeToApiType(item.product_type);
            return checkAvailability({ diamondId: diamondId, diamondType: diamondType }).then(
              function (status) {
                return { item: item, diamondId: diamondId, diamondType: diamondType, status: status };
              }
            );
          })
        ).then(function (results) {
          var sold = results.filter(function (r) {
            return r.status === "soldout";
          });

          if (!sold.length) {
            var anyUnknown = results.some(function (r) {
              return r.status === "unknown";
            });
            return anyUnknown ? "unverified" : "ok";
          }

          // Don't remove yet — surface it via the popup and let the shopper confirm by
          // clicking through. The popup's button (cart-template.liquid) performs the
          // actual removal + gift re-check, then redirects. lineKey/diamondId travel
          // along so that click handler can act without re-fetching/re-checking anything.
          var soldList = sold.map(function (s) {
            return {
              label: describeDiamond(s.item),
              diamondType: s.diamondType,
              lineKey: s.item.key,
              diamondId: s.diamondId,
              // Carried so the "Find Another Diamond" landing is pre-filtered to the
              // shape this shopper was buying. Survives the sessionStorage hop below.
              shape: diamondShapeOf(s.item),
            };
          });
          /* Nothing on this page may reach checkout with a sold-out stone in the cart,
             popup or no popup — the popup is what performs the removal. */
          try {
            if (window.cartUI && window.cartUI.blockCheckout) window.cartUI.blockCheckout("soldout");
          } catch (e) {
            /* ignore — the checkout gate still blocks on the 'soldout' status */
          }

          var shownOnCart = false;
          if (location.pathname.indexOf("/cart") === 0 && window.cartUI && window.cartUI.showSoldOutModal) {
            // A render failure must not swallow the notice: fall through to the
            // store-and-reload path below so the shopper still sees it.
            try {
              window.cartUI.showSoldOutModal(soldList);
              shownOnCart = true;
            } catch (e) {
              console.error("[ROADiamond] sold-out modal failed to render:", e);
            }
          }
          if (!shownOnCart) {
            sessionStorage.setItem("soldOutNotice", JSON.stringify(soldList));
            if (location.pathname.indexOf("/cart") === 0) location.replace("/cart");
            else location.href = "/cart";
          }
          return "soldout";
        });
      })
      .catch(function (e) {
        console.error("[ROADiamond] cart check failed:", e);
        return "unverified"; // could not verify; checkout will block, passive load will ignore
      });
  }

  window.ROADiamond = {
    checkAvailability: checkAvailability,
    fetchDiamondDetails: fetchDiamondDetails,
    checkCartLinePrice: checkCartLinePrice,
    repriceCartLine: repriceCartLine,
    typeToSearchUrl: typeToSearchUrl,
    diamondShapeOf: diamondShapeOf,
    removeSoldOutLines: removeSoldOutLines,
    productTypeToApiType: productTypeToApiType,
    isDiamondLineItem: isDiamondLineItem,
    clearBuilderSelection: clearBuilderSelection,
    describeDiamond: describeDiamond,
    verifyCartDiamonds: verifyCartDiamonds,
    verifyCartDiamondPrices: verifyCartDiamondPrices,
    _forceSoldOut: false, // set true in DevTools to simulate sold-out everywhere
    _forcePriceChange: false, // set true in DevTools to simulate a price change everywhere (the cart reprice creates a real product)
  };
})();
